import { Link } from 'react-router-dom';
import { promoTexts } from '../../../mocks/pharmacy';
import { useScrollReveal, useStaggerReveal } from '@/hooks/useScrollReveal';

const marqueeContent = [...promoTexts, ...promoTexts];

const PROMO_CARDS = [
  {
    icon: 'ri-gift-2-line',
    title: 'СКИДКА 30%',
    desc: 'На все витамины и БАД до конца недели',
    color: '#ff2d78',
    code: 'VITAMINS30',
    to: '/promos',
  },
  {
    icon: 'ri-truck-line',
    title: 'БЕСПЛАТНАЯ ДОСТАВКА',
    desc: 'При заказе от 1500₽ — доставка в подарок',
    color: '#39ff14',
    code: 'FREESHIP',
    to: '/delivery',
  },
  {
    icon: 'ri-flashlight-line',
    title: 'КЭШБЭК 15%',
    desc: 'Бонусы на каждый заказ новым клиентам',
    color: '#00f5ff',
    code: 'NEW15',
    to: '/loyalty',
  },
];

const AnimatedPromoBanner = () => {
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal({ threshold: 0.2 });
  const { ref: marquee1Ref, isVisible: marquee1Visible } = useScrollReveal({ threshold: 0.15 });
  const { ref: marquee2Ref, isVisible: marquee2Visible } = useScrollReveal({ threshold: 0.15 });
  const { containerRef: cardsRef, visibleItems } = useStaggerReveal(3, { threshold: 0.1 });

  return (
    <section id="promo" className="relative overflow-hidden" style={{ background: '#08080f' }}>
      {/* Top neon line */}
      <div
        className="h-px w-full"
        style={{ background: 'linear-gradient(90deg, transparent, #ff2d78 20%, #00f5ff 50%, #39ff14 80%, transparent)' }}
      />

      {/* Main Promo Area */}
      <div className="py-16 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div
            ref={headerRef as React.RefObject<HTMLDivElement>}
            className={`text-center mb-10 reveal-hidden ${headerVisible ? 'reveal-up' : ''}`}
          >
            <div className="inline-flex items-center gap-3 mb-3">
              <div className="h-px w-8 bg-neon-pink" style={{ boxShadow: '0 0 8px #ff2d78' }} />
              <span className="font-mono text-xs text-neon-pink/70 tracking-[0.3em]">ОГРАНИЧЕННОЕ ВРЕМЯ</span>
              <div className="h-px w-8 bg-neon-pink" style={{ boxShadow: '0 0 8px #ff2d78' }} />
            </div>
            <h2 className="font-orbitron font-black text-3xl md:text-5xl text-white mb-2">
              ГОРЯЩИЕ{' '}
              <span
                className="text-neon-pink animate-flicker"
                style={{ textShadow: '0 0 20px #ff2d78, 0 0 60px #ff2d78' }}
              >
                АКЦИИ
              </span>
            </h2>
          </div>

          {/* Scrolling Banner Row 1 */}
          <div
            ref={marquee1Ref as React.RefObject<HTMLDivElement>}
            className={`relative rounded-2xl overflow-hidden mb-4 japanese-border reveal-hidden ${marquee1Visible ? 'reveal-left' : ''}`}
            style={{ background: 'rgba(255,45,120,0.05)' }}
          >
            <div className="absolute inset-0 scanline opacity-20" />
            <div className="flex overflow-hidden py-4">
              <div className="flex animate-marquee whitespace-nowrap">
                {marqueeContent.map((text, idx) => (
                  <span key={idx} className="flex items-center mx-8">
                    <span
                      className="font-mono text-sm md:text-base font-bold text-neon-yellow tracking-widest"
                      style={{ textShadow: '0 0 10px #f0ff00, 0 0 20px #f0ff00' }}
                    >
                      {text}
                    </span>
                    <span className="mx-6 text-neon-pink" style={{ textShadow: '0 0 8px #ff2d78' }}>
                      ◆
                    </span>
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Scrolling Banner Row 2 (reverse) */}
          <div
            ref={marquee2Ref as React.RefObject<HTMLDivElement>}
            className={`relative rounded-2xl overflow-hidden mb-10 japanese-border reveal-hidden ${marquee2Visible ? 'reveal-right' : ''}`}
            style={{ background: 'rgba(0,245,255,0.05)', animationDelay: '100ms' }}
          >
            <div className="absolute inset-0 scanline opacity-20" />
            <div className="flex overflow-hidden py-4">
              <div className="flex animate-marquee2 whitespace-nowrap">
                {[...marqueeContent].reverse().map((text, idx) => (
                  <span key={idx} className="flex items-center mx-8">
                    <span
                      className="font-mono text-sm md:text-base font-bold text-neon-cyan tracking-widest"
                      style={{ textShadow: '0 0 10px #00f5ff, 0 0 20px #00f5ff' }}
                    >
                      {text}
                    </span>
                    <span className="mx-6 text-neon-green" style={{ textShadow: '0 0 8px #39ff14' }}>
                      ◇
                    </span>
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Promo Cards */}
          <div
            ref={cardsRef as React.RefObject<HTMLDivElement>}
            className="grid grid-cols-1 md:grid-cols-3 gap-4"
          >
            {PROMO_CARDS.map((card, idx) => (
              <Link
                key={card.title}
                to={card.to}
                className={`card-cyber rounded-2xl p-6 relative overflow-hidden cursor-pointer group block reveal-hidden ${visibleItems[idx] ? 'reveal-scale' : ''}`}
                style={{ borderColor: `${card.color}30`, animationDelay: `${idx * 100}ms` }}
              >
                <div
                  className="absolute top-0 left-0 right-0 h-0.5"
                  style={{ background: `linear-gradient(90deg, transparent, ${card.color}, transparent)` }}
                />
                <div
                  className="absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-10 group-hover:opacity-20 transition-opacity"
                  style={{ background: card.color, filter: 'blur(20px)' }}
                />
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-xl mb-4"
                  style={{ background: `${card.color}15`, border: `1px solid ${card.color}40` }}
                >
                  <i className={`${card.icon} text-2xl`} style={{ color: card.color, filter: `drop-shadow(0 0 6px ${card.color})` }} />
                </div>
                <h3
                  className="font-orbitron font-bold text-sm tracking-wider mb-1"
                  style={{ color: card.color, textShadow: `0 0 10px ${card.color}` }}
                >
                  {card.title}
                </h3>
                <p className="font-mono text-xs text-white/60 mb-4 leading-relaxed">{card.desc}</p>
                <div
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg"
                  style={{ background: `${card.color}15`, border: `1px solid ${card.color}30` }}
                >
                  <i className="ri-coupon-3-line text-xs" style={{ color: card.color }} />
                  <span className="font-mono text-xs font-bold tracking-widest" style={{ color: card.color }}>
                    {card.code}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom neon line */}
      <div
        className="h-px w-full"
        style={{ background: 'linear-gradient(90deg, transparent, #39ff14 20%, #00f5ff 50%, #ff2d78 80%, transparent)' }}
      />
    </section>
  );
};

export default AnimatedPromoBanner;
